<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Transferências entre Contas</h5>
      <b-button
        v-if="hasPermission('adicionar_transferencias_contas')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill mr-2" title="Nova Transferência"></i>
      </b-button>
    </div>

    <div class="d-flex mb-3" style="gap: 10px;">
      <select
        v-model="filters.conta_id"
        class="form-control"
        @change="filtrar"
      >
        <option value="">Todas as contas</option>
        <option v-for="conta in contas" :key="conta.id" :value="conta.id">
          {{ conta.apelido }} - {{ conta.agencia }} - {{ conta.numero_conta }}
        </option>
      </select>

      <input
        v-model="filters.data_inicio"
        type="date"
        class="form-control"
        @change="filtrar"
      />

      <input
        v-model="filters.data_fim"
        type="date"
        class="form-control"
        @change="filtrar"
      />

      <input
        v-model="filters.historico"
        placeholder="Filtrar por histórico"
        class="form-control"
        @input="filtrar"
      />

      <b-button
        variant="secondary"
        class="d-flex align-items-center"
        @click="limparFiltros"
      >
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('id')">
              # {{ arrow('id') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('data_transferencia')">
              Data {{ arrow('data_transferencia') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('conta_origem')">
              Conta Origem {{ arrow('conta_origem') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('conta_destino')">
              Conta Destino {{ arrow('conta_destino') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('historico')">
              Histórico {{ arrow('historico') }}
            </button>
          </th>
          <th class="text-end">
            <button class="btn btn-link p-0" @click="changeSort('valor')">
              Valor {{ arrow('valor') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!transferencias.length">
          <td colspan="7" class="text-center text-muted">Nenhuma transferência encontrada.</td>
        </tr>
        <tr v-for="item in transferencias" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ formatDate(item.data_transferencia) }}</td>
          <td>
            {{ item.conta_origem_apelido }} - {{ item.conta_origem_agencia }} - {{ item.conta_origem_numero_conta }}
          </td>
          <td>
            {{ item.conta_destino_apelido }} - {{ item.conta_destino_agencia }} - {{ item.conta_destino_numero_conta }}
          </td>
          <td>{{ item.historico || '—' }}</td>
          <td class="text-end">{{ formatCurrency(item.valor) }}</td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_transferencias_contas')"
              class="bi bi-eye-fill text-primary mx-1"
              title="Detalhar"
              @click="detalhar(item.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_transferencias_contas')"
              class="bi bi-pencil-fill text-warning mx-1"
              title="Editar"
              @click="editar(item.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_transferencias_contas')"
              class="bi bi-trash-fill text-danger mx-1"
              title="Excluir"
              @click="excluir(item.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>

      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import axios from '@/services/axios'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'

const router = useRouter()
const { proxy } = getCurrentInstance()
const { fetchPermissions, hasPermission } = usePermissions()

const contas = ref([])

const {
  items: transferencias,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  '/financeiro/transferencias-contas',
  { key: 'data_transferencia', order: 'desc' }
)

const pageOptions = [10, 15, 20, 25, 50]

const startRow = computed(() => totalItems.value === 0 ? 0 : ((page.value - 1) * limit.value + 1))
const endRow = computed(() => Math.min(page.value * limit.value, totalItems.value))

function formatCurrency(n) {
  const v = typeof n === 'number' ? n : parseFloat(n || 0)
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(isNaN(v) ? 0 : v)
}

function formatDate(d) {
  if (!d) return '—'
  const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`)
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('pt-BR')
}

async function carregarContas() {
  try {
    const { data } = await axios.get('/financeiro/contas-financeiras', {
      params: { page: 1, limit: 1000 }
    })
    contas.value = Array.isArray(data?.data) ? data.data : []
  } catch (error) {
    console.error('Erro ao carregar contas financeiras:', error)
  }
}

async function filtrar() {
  page.value = 1
  await load()
}

async function limparFiltros() {
  filters.value = {
    conta_id: '',
    data_inicio: '',
    data_fim: '',
    historico: ''
  }
  page.value = 1
  await load()
}

async function changeLimit() {
  page.value = 1
  await load()
}

async function changeSort(field) {
  sort(field)
  await load()
}

function irParaCadastro() {
  router.push('/financeiro/transferencias-contas/novo')
}

function detalhar(id) {
  router.push(`/financeiro/transferencias-contas/${id}/detalhar`)
}

function editar(id) {
  router.push(`/financeiro/transferencias-contas/${id}`)
}

async function excluir(id) {
  const ok = await proxy.$confirm?.('Tem certeza que deseja excluir esta transferência?', 'Confirmação')
  if (!ok) return

  try {
    await axios.delete(`/financeiro/transferencias-contas/${id}`)
    proxy.$toast?.success?.('Transferência excluída!')
    await load()
  } catch (error) {
    console.error('Erro ao excluir transferência:', error)
    proxy.$toast?.error?.(error?.response?.data?.error || 'Erro ao excluir transferência.')
  }
}

onMounted(async () => {
  await fetchPermissions()
  filters.value = {
    conta_id: '',
    data_inicio: '',
    data_fim: '',
    historico: ''
  }
  await carregarContas()
  await load()
})
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>